import {
  APIFieldAttributes,
  APIFieldType,
  APISurveyAttributes,
  APITagAttributes,
  ArdoqId,
  BaseReferenceQuestionProperties,
  CheckboxDisplayCondition,
  ListDisplayCondition,
  PersistedSurveyQuestion,
  SurveyQuestionType,
  SurveyQuestionValidator,
  UnpersistedSurveyQuestion,
  UnsetDisplayCondition,
  ValidDisplayConditions,
} from '@ardoq/api-types';
import {
  AllPossibleQuestions,
  MaybePersistedSurvey,
  SurveyQuestionError,
  SurveyQuestionValidation,
  SurveyQuestionValidations,
  SurveyQuestionWarning,
} from '../types';
import {
  currentUserHasNoAccessToWorkspace,
  currentUserHasNoEditAccessToWorkspace,
  getComponentTypeAndModelId,
} from '../SurveyEditor/utils';
import { getReferenceData, ReferenceDataLookups } from '../referenceUtil';
import { WorkspaceModelDataByWorkspaceId } from 'streams/models/workspaceModelDataByWorkspaceId$';
import { FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import {
  ExternallyManagedFieldNamesByWorkspaceId,
  ReadonlyFieldData,
} from 'surveyAdmin/streams/readonlyFieldData$';
import { fieldOps } from 'models/utils/fieldOps';
import { isTruthy, isArdoqError } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import { isNumber } from 'lodash';
import { logError } from '@ardoq/logging';
import { searchAPIService as searchService } from 'search/searchAPIService';
import { dateRangeOperations } from '@ardoq/date-range';
import { emailValidator, numberValidator } from 'scopeData/editors/validators';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { composeSearchQuery } from 'search/AdvancedSearch/utils';
import { PermissionContext } from '@ardoq/access-control';
import { markdownToTextPreview } from '@ardoq/markdown';
import {
  isReferenceQuestion,
  isChildQuestion,
  isConditionQuestion,
  isAttributeQuestion,
  isTagQuestion,
  isFieldQuestion,
  isHiddenFieldQuestion,
  isDependentQuestion,
} from './questionPredicates';
import { getSubQuestions } from '@ardoq/surveys-ui';
import { SurveyEditorState } from 'surveyAdmin/SurveyEditor/streams/types';
import { QuestionInteractionState } from 'surveyAdmin/SurveyEditor/streams/fieldInteractionTypes';
import { REQUIRED_FIELD_MESSAGE } from 'surveyAdmin/consts';
import { t, type I18nLocale } from '@ardoq/i18n';
import { FieldQuestionData } from './types';
import { SelectOption } from '@ardoq/select';
import { IconName } from '@ardoq/icons';

export const removeComponentCreationFromQuestions = (
  questions: AllPossibleQuestions[]
): AllPossibleQuestions[] =>
  questions.map(question => {
    if (isReferenceQuestion(question)) {
      return {
        ...question,
        properties: {
          ...question.properties,
          allowComponentCreation: false,
        },
      };
    }
    if (isConditionQuestion(question)) {
      return {
        ...question,
        properties: {
          ...question.properties,
          questions: removeComponentCreationFromQuestions(
            question.properties.questions ?? []
          ),
        },
      };
    }
    return question;
  }) as AllPossibleQuestions[];

const getTagsOfWorkspace = (
  workspaceId: ArdoqId,
  tags: APITagAttributes[] | null
): APITagAttributes[] => {
  if (!tags) return [];

  return tags.filter(tag => tag.rootWorkspace === workspaceId);
};

export const checkIfSelectedNewComponentParentDoesNotExist = async (
  surveyProperties: APISurveyAttributes | BaseReferenceQuestionProperties
) => {
  const { allowComponentCreation, newComponentParentId } = surveyProperties;
  if (!newComponentParentId || !allowComponentCreation) {
    return false;
  }
  const query = composeSearchQuery({
    attributes: [
      {
        attribute: 'id',
        value: newComponentParentId,
      },
    ],
  });
  const result = await searchService.componentSearch(query);

  if (isArdoqError(result)) {
    logError(result);
    return true;
  }
  const { results } = result;
  return !results.length;
};

type IsReadonlyFieldParams = {
  fieldName: string;
  workspaceId: string | null;
  parentQuestionIsReferenceQuestion: boolean;
  readonlyFieldData: ReadonlyFieldData;
};

const isExternallyManagedField = ({
  fieldName,
  workspaceId,
  parentQuestionIsReferenceQuestion,
  externallyManagedFieldNamesByWorkspaceId,
}: {
  fieldName: string;
  workspaceId: string | null;
  parentQuestionIsReferenceQuestion: boolean;
  externallyManagedFieldNamesByWorkspaceId: ExternallyManagedFieldNamesByWorkspaceId;
}): boolean => {
  if (!workspaceId) {
    return false;
  }
  const workspaceFields = externallyManagedFieldNamesByWorkspaceId[workspaceId];
  if (!workspaceFields) {
    return false;
  }
  return parentQuestionIsReferenceQuestion
    ? workspaceFields.referenceFieldNames.has(fieldName)
    : workspaceFields.componentFieldNames.has(fieldName);
};

export const isReadonlyField = ({
  fieldName,
  workspaceId,
  parentQuestionIsReferenceQuestion,
  readonlyFieldData,
}: IsReadonlyFieldParams): boolean => {
  return (
    isExternallyManagedField({
      fieldName,
      workspaceId,
      parentQuestionIsReferenceQuestion,
      externallyManagedFieldNamesByWorkspaceId:
        readonlyFieldData.externallyManagedFieldNamesByWorkspaceId,
    }) ||
    readonlyFieldData.calculatedFieldNames.has(fieldName) ||
    fieldName === 'component-key'
  );
};

const componentTypeIsHierarchyRoot = (
  rootComponentTypeNames: readonly string[],
  componentTypeName: string
) => rootComponentTypeNames.includes(componentTypeName);

const questionIsRedundant = (
  surveyQuestionWrapper: MaybePersistedSurvey,
  question: AllPossibleQuestions,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
): SurveyQuestionError | undefined => {
  if (!surveyQuestionWrapper.workspace) return;
  const workspaceModelData =
    workspaceModelDataByWorkspaceId[surveyQuestionWrapper.workspace];
  // It's too early to decide if parent is missing if component type and workspace is not yet selected
  if (!workspaceModelData || !surveyQuestionWrapper.componentTypeName) {
    return;
  }
  if (!workspaceModelData.modelIsRigid) {
    return;
  }
  if (
    componentTypeIsHierarchyRoot(
      workspaceModelData.rootComponentTypeNames,
      surveyQuestionWrapper.componentTypeName
    ) &&
    isAttributeQuestion(question) &&
    question.properties.attributeName === 'parent'
  ) {
    return SurveyQuestionError.PARENT_NOT_ALLOWED;
  }
};

const typeFields = (
  fieldsByType: Map<string, APIFieldAttributes[]>,
  modelId: ArdoqId,
  typeId: string
) =>
  dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(
      fieldsByType.get(toFieldTypeKey(modelId, typeId)) ?? []
    )
    .fields.map(field =>
      dateRangeOperations.isDateRangeField(field)
        ? {
            ...field,
            name: `${field.dateTimeFields.start.name},${field.dateTimeFields.end.name}`,
          }
        : field
    );

export const componentFields = (
  fieldsByComponentType: FieldsState['byComponentType'],
  modelId: ArdoqId,
  componentTypeId: string
) => typeFields(fieldsByComponentType, modelId, componentTypeId);

export const referenceFields = (
  fieldsByReferenceType: FieldsState['byReferenceType'],
  modelId: ArdoqId,
  referenceTypeId: string
) => typeFields(fieldsByReferenceType, modelId, referenceTypeId);

export const hasQuestionValidationOfSeverity = (
  validations: SurveyQuestionValidations | undefined,
  severity: SurveyQuestionValidation['severity']
): boolean => {
  if (!validations) return false;
  return (
    validations.questionValidations.some(
      validation => validation.severity === severity
    ) ||
    (validations.subQuestionValidations &&
      validations.subQuestionValidations.some(validations =>
        hasQuestionValidationOfSeverity(validations, severity)
      )) ||
    false
  );
};

export const hasComponentType = (
  componentTypeName?: string | null,
  selectedTypeId?: string
) => {
  if (!selectedTypeId && !componentTypeName)
    return SurveyQuestionError.COMPONENT_TYPE_MISSING;
  if (!selectedTypeId && componentTypeName)
    return SurveyQuestionError.SELECTED_COMPONENT_TYPE_DOES_NOT_EXIST;
  if (componentTypeName) return;
  return SurveyQuestionError.COMPONENT_TYPE_MISSING;
};

type GetComponentTypeFieldsParams = {
  workspaceId: ArdoqId;
  componentTypeName: string;
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId;
  fieldsByComponentType: FieldsState['byComponentType'];
};

export const getComponentTypeFields = ({
  workspaceId,
  componentTypeName,
  workspaceModelDataByWorkspaceId,
  fieldsByComponentType,
}: GetComponentTypeFieldsParams): APIFieldAttributes[] => {
  const { selectedModelId, selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    workspaceId,
    componentTypeName
  );
  if (!selectedModelId || !selectedTypeId) return [];
  return (
    fieldsByComponentType.get(
      toFieldTypeKey(selectedModelId, selectedTypeId)
    ) ?? []
  );
};

const referenceQuestionHasComponentType = (
  q: AllPossibleQuestions,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  if (!isReferenceQuestion(q)) return;
  const { componentTypeName } = q.properties;
  const { selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    q.properties.workspaceId,
    componentTypeName
  );

  return hasComponentType(componentTypeName, selectedTypeId);
};

const referenceQuestionHasWorkspace = (
  q: AllPossibleQuestions,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  if (!isReferenceQuestion(q)) return;
  const { selectedModelId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    q.properties.workspaceId,
    q.properties.componentTypeName
  );
  if (selectedModelId) return;
  const { workspaceId } = q.properties;
  if (workspaceId) return;
  return SurveyQuestionError.WORKSPACE_MISSING;
};

const tagQuestionIsMissingTags = (
  q: AllPossibleQuestions,
  surveyAttributes: MaybePersistedSurvey,
  tags: APITagAttributes[] | null
) => {
  if (
    !isTagQuestion(q) ||
    !surveyAttributes.workspace ||
    getTagsOfWorkspace(surveyAttributes.workspace, tags).length
  )
    return;
  return SurveyQuestionError.WORKSPACE_TAGS_MISSING;
};

const referenceQuestionTypeError = (
  permissionsContext: PermissionContext,
  q: AllPossibleQuestions,
  referenceTypeId?: string | number
) => {
  if (!isReferenceQuestion(q)) return;
  const workspaceId = q.properties.workspaceId;
  const referenceAndWorkspaceSelected =
    q.properties.referenceTypeName && workspaceId;
  if (!referenceAndWorkspaceSelected)
    return SurveyQuestionError.REFERENCE_TYPE_MISSING;

  const hasNoAccessToReferenceWorkspace = currentUserHasNoAccessToWorkspace(
    permissionsContext,
    workspaceId
  );

  const hasNoEditAccesstoReferenceWorkspace =
    currentUserHasNoEditAccessToWorkspace(permissionsContext, workspaceId);

  if (hasNoAccessToReferenceWorkspace)
    return SurveyQuestionWarning.NO_READ_ACCESS_TO_SELECTED_REFERENCE_TYPE;

  if (hasNoEditAccesstoReferenceWorkspace)
    return SurveyQuestionWarning.NO_EDIT_ACCESS_TO_SELECTED_REFERENCE_TYPE;

  if (referenceTypeId === undefined)
    return SurveyQuestionError.SELECTED_REFERENCE_TYPE_DOES_NOT_EXIST;
};

const referenceQuestionParentIsNeededOrInvalid = async (
  surveyQuestion: AllPossibleQuestions,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId
) => {
  if (!isReferenceQuestion(surveyQuestion)) return;
  const { workspaceId, componentTypeName } = surveyQuestion.properties;
  if (
    surveyQuestion.properties.allowComponentCreation &&
    workspaceId &&
    componentTypeName
  ) {
    const workspaceModelData = workspaceModelDataByWorkspaceId[workspaceId];
    if (!workspaceModelData) return;
    if (surveyQuestion.properties.newComponentParentId) {
      const missingComponent =
        await checkIfSelectedNewComponentParentDoesNotExist(
          surveyQuestion.properties
        );

      if (missingComponent) return SurveyQuestionError.REFERENCE_PARENT_INVALID;
    }
    if (
      workspaceModelData.modelIsRigid &&
      !surveyQuestion.properties.newComponentParentId &&
      !componentTypeIsHierarchyRoot(
        workspaceModelData.rootComponentTypeNames,
        componentTypeName
      )
    )
      return SurveyQuestionError.REFERENCE_PARENT_MISSING;
  }
};

const questionHasInvalidRangeRules = (surveyQuestion: AllPossibleQuestions) => {
  if (isReferenceQuestion(surveyQuestion) || isTagQuestion(surveyQuestion)) {
    const validators = surveyQuestion.validators || [];

    const rangeValidator: SurveyQuestionValidator | undefined =
      validators.find(isRangeValidator);
    if (!rangeValidator) return;
    if (
      isNumber(rangeValidator?.max) &&
      isNumber(rangeValidator?.min) &&
      rangeValidator.max < rangeValidator.min
    )
      return SurveyQuestionError.RANGE_RULES_INVALID;
  }
};

const referenceQuestionHasPreventCreationAndDeletionithoutSubQuestions = (
  surveyQuestion: AllPossibleQuestions
) => {
  if (isReferenceQuestion(surveyQuestion)) {
    const hasSubQuestions =
      surveyQuestion.properties.questions &&
      surveyQuestion.properties.questions.some(
        q =>
          q.type === SurveyQuestionType.FIELD ||
          q.type === SurveyQuestionType.ATTRIBUTE
      );

    if (
      !hasSubQuestions &&
      surveyQuestion.properties.preventReferenceModification
    )
      return SurveyQuestionError.PREVENTING_REFERENCE_MODIFICATION_REQUIRES_SUBQUESTIONS;
  }
};

const referenceQuestionHasCreateMultipleWithoutSubQuestions = (
  surveyQuestion: AllPossibleQuestions
) => {
  if (isReferenceQuestion(surveyQuestion)) {
    const hasSubQuestions =
      surveyQuestion.properties.questions &&
      surveyQuestion.properties.questions.some(
        q =>
          q.type === SurveyQuestionType.FIELD ||
          q.type === SurveyQuestionType.ATTRIBUTE
      );

    if (!hasSubQuestions && surveyQuestion.properties.allowMultipleReferences)
      return SurveyQuestionError.MULTIPLE_REFERENCES_REQUIRES_SUBQUESTIONS;
  }
};

type ReferenceQuestionInvalidFieldIdentifierParams = {
  surveyQuestion: AllPossibleQuestions;
  workspacesById: ReferenceDataLookups['workspacesById'];
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId;
  fieldsByComponentType: FieldsState['byComponentType'];
};

const referenceQuestionInvalidFieldIdentifier = ({
  surveyQuestion,
  workspacesById,
  workspaceModelDataByWorkspaceId,
  fieldsByComponentType,
}: ReferenceQuestionInvalidFieldIdentifierParams) => {
  if (!isReferenceQuestion(surveyQuestion)) return;
  const identifyingField = surveyQuestion.properties.additionalIdentifier;
  if (!identifyingField) return;

  const componentTypeName = surveyQuestion.properties.componentTypeName;
  const workspaceId = surveyQuestion.properties.workspaceId;
  if (!workspaceId || !workspacesById[workspaceId] || !componentTypeName)
    return;
  const componentTypeFields = getComponentTypeFields({
    workspaceId,
    componentTypeName,
    workspaceModelDataByWorkspaceId,
    fieldsByComponentType,
  });

  const foundField = componentTypeFields.find(
    field => field.name === identifyingField
  );

  if (!foundField)
    return SurveyQuestionError.IDENTIFYING_FIELD_MISSING_OR_INVALID;
};

const getAttributes = (i18nLocale: I18nLocale) => [
  {
    label: t(i18nLocale, 'surveyAdmin:questionAttributes.name', 'Name'),
    attributeName: 'name',
    isComponent: true,
  },
  {
    label: t(i18nLocale, 'surveyAdmin:questionAttributes.parent', 'Parent'),
    attributeName: 'parent',
    isComponent: true,
  },
  {
    label: t(
      i18nLocale,
      'surveyAdmin:questionAttributes.description',
      'Description'
    ),
    attributeName: 'description',
    isComponent: true,
    isReference: true,
  },
  {
    label: t(
      i18nLocale,
      'surveyAdmin:questionAttributes.displayText',
      'Display text'
    ),
    attributeName: 'displayText',
    isReference: true,
  },
  {
    label: t(i18nLocale, 'surveyAdmin:questionAttributes.ardoqId', 'Ardoq ID'),
    attributeName: 'component-key',
    isComponent: true,
  },
];

export const getComponentAttributes = (i18nLocale: I18nLocale) =>
  getAttributes(i18nLocale).filter(({ isComponent }) => isComponent);

export const getReferenceAttributes = (i18nLocale: I18nLocale) =>
  getAttributes(i18nLocale).filter(({ isReference }) => isReference);

// Must mirror the isReference/isComponent flags in getAttributes() above — adding a new attribute there requires updating these arrays.
const REFERENCE_ATTRIBUTE_NAMES = ['description', 'displayText'];
const COMPONENT_ATTRIBUTE_NAMES = [
  'name',
  'parent',
  'description',
  'component-key',
];

const attributeIsUnavailable = (
  question: AllPossibleQuestions,
  isReferenceQuestion: boolean
) => {
  if (!isAttributeQuestion(question)) return;
  const availableAttributeNames = isReferenceQuestion
    ? REFERENCE_ATTRIBUTE_NAMES
    : COMPONENT_ATTRIBUTE_NAMES;

  const foundAttribute = availableAttributeNames.includes(
    question.properties.attributeName
  );

  if (!foundAttribute) return SurveyQuestionError.FIELD_NOT_AVAILABLE;
};

const fieldIsUnavailable = (
  fields: APIFieldAttributes[],
  question: AllPossibleQuestions
) => {
  if (isFieldQuestion(question) || isHiddenFieldQuestion(question)) {
    const foundField =
      question.properties.fieldName &&
      fields.find(field => field.name === question.properties.fieldName);

    if (question.properties.fieldName && !foundField) {
      return SurveyQuestionError.FIELD_NOT_AVAILABLE;
    }
  }
};

const childQuestionIsMissingComponentType = (
  question: AllPossibleQuestions
) => {
  if (isChildQuestion(question) && !question.properties.componentTypeName) {
    return SurveyQuestionError.COMPONENT_TYPE_MISSING;
  }
};

const fieldNotSelected = (question: AllPossibleQuestions) => {
  if (
    (isFieldQuestion(question) || isHiddenFieldQuestion(question)) &&
    !question.properties.fieldName
  ) {
    return SurveyQuestionError.FIELD_NOT_SELECTED;
  }
  if (isAttributeQuestion(question) && !question.properties.attributeName) {
    return SurveyQuestionError.FIELD_NOT_SELECTED;
  }
};

export const isUnsetCondition = (
  condition: ValidDisplayConditions
): condition is UnsetDisplayCondition =>
  condition && (condition as UnsetDisplayCondition).type === 'unset';

const isUnsetType = (condition: ValidDisplayConditions) =>
  condition.type === 'unset';

const isCheckboxType = (
  condition: ValidDisplayConditions
): condition is CheckboxDisplayCondition =>
  !isUnsetCondition(condition) &&
  (condition.value === true || condition.value === false);

const isListType = (
  condition: ValidDisplayConditions
): condition is ListDisplayCondition =>
  !isUnsetCondition(condition) && typeof condition.value === 'string';

const matchesAnAllowedValue = (
  condition: ValidDisplayConditions,
  allowedValues: ReadonlySet<string>
) => isListType(condition) && allowedValues.has(condition.value);

type DisplayConditionContext = {
  parentQuestionField?: APIFieldAttributes;
  allowedValues: ReadonlySet<string>;
};

const toDisplayConditionContext = (
  fields: APIFieldAttributes[],
  parentQuestion?: AllPossibleQuestions
): DisplayConditionContext => {
  const parentQuestionField = isFieldQuestion(parentQuestion)
    ? fields.find(field => field.name === parentQuestion.properties.fieldName)
    : undefined;
  return {
    parentQuestionField,
    allowedValues: new Set(
      parentQuestionField ? fieldOps.getAllowedValues(parentQuestionField) : []
    ),
  };
};

const validateDisplayConditions = (
  displayConditions: ValidDisplayConditions[],
  { parentQuestionField, allowedValues }: DisplayConditionContext
) => {
  if (!parentQuestionField?.type) return SurveyQuestionError.FIELD_NOT_SELECTED;

  switch (parentQuestionField.type) {
    case APIFieldType.LIST: {
      if (!allowedValues.size) return false;
      return displayConditions.every(
        condition =>
          isUnsetType(condition) ||
          matchesAnAllowedValue(condition, allowedValues)
      );
    }
    case APIFieldType.CHECKBOX:
      return displayConditions.every(
        condition => isUnsetType(condition) || isCheckboxType(condition)
      );
    default:
      return false;
  }
};

const getHiddenFieldAnswerError = (
  question: AllPossibleQuestions,
  fields: APIFieldAttributes[]
) => {
  if (question.type !== SurveyQuestionType.HIDDEN_FIELD) {
    return undefined;
  }
  const fieldType = fields.find(
    field => field.name === question.properties.fieldName
  )?.type;
  const resetValue = question.properties.resetValue;
  switch (fieldType) {
    case APIFieldType.LIST:
      if (resetValue === undefined) {
        return SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE;
      }
      break;
    case APIFieldType.SELECT_MULTIPLE_LIST:
      if (resetValue === undefined || resetValue.length === 0) {
        return SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE;
      }
      break;
    case APIFieldType.TEXT:
    case APIFieldType.TEXT_AREA:
    case APIFieldType.URL:
      if (resetValue === undefined || resetValue === '') {
        return SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE;
      }
      break;
    case APIFieldType.NUMBER:
      if (isNaN(resetValue) || numberValidator(resetValue)) {
        return SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE;
      }
      break;
    case APIFieldType.EMAIL:
      if (resetValue === undefined || emailValidator(resetValue)) {
        return SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE;
      }
      break;
    // Missing cases are CHECKBOX, DATE_TIME, USER and DATE_RANGE
    // CHECKBOX, DATE_TIME and USER are never invalid
    // undefined DATE_TIME or USER leads to automatic value(current time/user)
    // Date range is not currently supported for hidden fields
    default:
      return;
  }
};

const dependentQuestionHasInvalidDisplayConditions = (
  question: AllPossibleQuestions,
  displayConditionContext: DisplayConditionContext,
  parentQuestion?: AllPossibleQuestions
) => {
  if (!isDependentQuestion(question) || !isFieldQuestion(parentQuestion))
    return;
  if (
    !isReferenceQuestion(question) &&
    !isAttributeQuestion(question) &&
    !isFieldQuestion(question)
  ) {
    return;
  }

  if (!question.properties.displayConditions?.length)
    return SurveyQuestionError.DISPLAY_CONDITIONS_MISSING;

  const areDisplayConditionsValid = validateDisplayConditions(
    question.properties.displayConditions,
    displayConditionContext
  );

  if (!areDisplayConditionsValid)
    return SurveyQuestionError.DISPLAY_CONDITIONS_INVALID;
};

const getAppropriateWorksaceIdAndComponentTypeName = ({
  question,
  survey,
  subQuestionIsReferenceQuestion,
}: {
  question?: AllPossibleQuestions;
  survey: MaybePersistedSurvey;
  subQuestionIsReferenceQuestion: boolean;
}) => {
  if (subQuestionIsReferenceQuestion && isReferenceQuestion(question)) {
    if (question.properties.outgoing) {
      return {
        workspaceId: survey.workspace,
        componentTypeName: survey.componentTypeName,
      };
    }
    return {
      workspaceId: question.properties.workspaceId,
      componentTypeName: question.properties.componentTypeName,
    };
  }
  return {
    workspaceId: survey.workspace,
    componentTypeName: survey.componentTypeName,
  };
};

type SubQuestionFieldsParams = {
  subQuestionIsReferenceQuestion: boolean;
  selectedModelId: ArdoqId | undefined;
  selectedTypeId: string | undefined;
  referenceTypeId: number | undefined;
  fieldsState: FieldsState;
};

const getSubQuestionFields = ({
  subQuestionIsReferenceQuestion,
  selectedModelId,
  selectedTypeId,
  referenceTypeId,
  fieldsState,
}: SubQuestionFieldsParams) => {
  if (!selectedModelId || !selectedTypeId) return [];
  if (!subQuestionIsReferenceQuestion) {
    return componentFields(
      fieldsState.byComponentType,
      selectedModelId,
      selectedTypeId
    );
  }
  if (referenceTypeId === undefined) return [];
  return referenceFields(
    fieldsState.byReferenceType,
    selectedModelId,
    String(referenceTypeId)
  );
};

export const getQuestionValidations = async ({
  question,
  survey,
  tags,
  permissionsContext,
  referenceDataLookups,
  workspaceModelDataByWorkspaceId,
  fieldsState,
  subQuestionIsReferenceQuestion = false,
}: {
  question?: AllPossibleQuestions;
  survey: MaybePersistedSurvey;
  tags: APITagAttributes[] | null;
  permissionsContext: PermissionContext;
  referenceDataLookups: ReferenceDataLookups;
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId;
  fieldsState: FieldsState;
  subQuestionIsReferenceQuestion?: boolean;
}): Promise<SurveyQuestionValidations[]> => {
  const { workspaceId, componentTypeName } =
    getAppropriateWorksaceIdAndComponentTypeName({
      question,
      survey,
      subQuestionIsReferenceQuestion,
    });
  const { selectedTypeId, selectedModelId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    workspaceId,
    componentTypeName
  );

  const referenceData = isReferenceQuestion(question)
    ? getReferenceData({
        surveyWorkspaceId: survey.workspace,
        surveyComponentTypeId: selectedTypeId,
        outgoing: question.properties.outgoing,
        workspaceId: question.properties.workspaceId,
        componentTypeName: question.properties.componentTypeName,
        referenceTypeName: question.properties.referenceTypeName ?? undefined,
        ...referenceDataLookups,
      })
    : undefined;

  const fields = getSubQuestionFields({
    subQuestionIsReferenceQuestion,
    selectedModelId,
    selectedTypeId,
    referenceTypeId: referenceData?.referenceTypeId,
    fieldsState,
  });

  const displayConditionContext = toDisplayConditionContext(fields, question);

  const questionToErrors = async (
    q: AllPossibleQuestions,
    surveyAttributes: MaybePersistedSurvey,
    tags: APITagAttributes[] | null,
    permissionsContext: PermissionContext
  ): Promise<SurveyQuestionValidation[]> => {
    const { selectedTypeId } =
      isReferenceQuestion(q) && q.properties.outgoing
        ? getComponentTypeAndModelId(
            workspaceModelDataByWorkspaceId,
            q.properties.workspaceId,
            q.properties.componentTypeName
          )
        : getComponentTypeAndModelId(
            workspaceModelDataByWorkspaceId,
            surveyAttributes.workspace,
            surveyAttributes.componentTypeName
          );

    const referenceData = isReferenceQuestion(q)
      ? getReferenceData({
          surveyWorkspaceId: surveyAttributes.workspace,
          surveyComponentTypeId: selectedTypeId,
          outgoing: q.properties.outgoing,
          workspaceId: q.properties.workspaceId,
          componentTypeName: q.properties.componentTypeName,
          referenceTypeName: q.properties.referenceTypeName ?? undefined,
          ...referenceDataLookups,
        })
      : undefined;

    const getReferenceQuestionValidations = async () => {
      const referenceQuestionValidations = [
        referenceQuestionHasWorkspace(q, workspaceModelDataByWorkspaceId),
        referenceQuestionHasComponentType(q, workspaceModelDataByWorkspaceId),
        referenceQuestionTypeError(
          permissionsContext,
          q,
          referenceData?.referenceTypeId
        ),
        await referenceQuestionParentIsNeededOrInvalid(
          q,
          workspaceModelDataByWorkspaceId
        ),
        referenceQuestionInvalidFieldIdentifier({
          surveyQuestion: q,
          workspacesById: referenceDataLookups.workspacesById,
          workspaceModelDataByWorkspaceId,
          fieldsByComponentType: fieldsState.byComponentType,
        }),
        referenceQuestionHasCreateMultipleWithoutSubQuestions(q),
        referenceQuestionHasPreventCreationAndDeletionithoutSubQuestions(q),
      ]
        .map(maybeSurveyQuestionValidation)
        .filter(isTruthy);

      if (
        referenceQuestionValidations.find(
          validation =>
            validation.message === SurveyQuestionError.WORKSPACE_MISSING ||
            validation.message ===
              SurveyQuestionWarning.NO_READ_ACCESS_TO_SELECTED_REFERENCE_TYPE
        )
      ) {
        // Drop component type & Reference type errors if workspace is missing.
        return referenceQuestionValidations.filter(
          v =>
            ![
              SurveyQuestionError.COMPONENT_TYPE_MISSING,
              SurveyQuestionError.SELECTED_COMPONENT_TYPE_DOES_NOT_EXIST,
              SurveyQuestionError.SELECTED_REFERENCE_TYPE_DOES_NOT_EXIST,
              SurveyQuestionError.REFERENCE_TYPE_MISSING,
            ].includes(v.message as SurveyQuestionError)
        );
      }
      return referenceQuestionValidations;
    };

    return [
      fieldIsUnavailable(fields, q),
      attributeIsUnavailable(q, isReferenceQuestion(question)),
      fieldNotSelected(q),
      childQuestionIsMissingComponentType(q),
      getHiddenFieldAnswerError(q, fields),
      dependentQuestionHasInvalidDisplayConditions(
        q,
        displayConditionContext,
        question
      ),
      tagQuestionIsMissingTags(q, surveyAttributes, tags),
      questionHasInvalidRangeRules(q),
      survey.workspace
        ? questionIsRedundant(survey, q, workspaceModelDataByWorkspaceId)
        : undefined,
    ]
      .map(maybeSurveyQuestionValidation)
      .concat(await getReferenceQuestionValidations())
      .filter(isTruthy);
  };

  const questions =
    isReferenceQuestion(question) ||
    isChildQuestion(question) ||
    isConditionQuestion(question)
      ? question.properties.questions
      : (survey.questions as AllPossibleQuestions[]);
  try {
    return await Promise.all(
      (questions || []).map(async q => {
        const subQuestionValidations =
          isReferenceQuestion(q) || isChildQuestion(q) || isConditionQuestion(q)
            ? await getQuestionValidations({
                question: q,
                survey,
                tags,
                permissionsContext,
                referenceDataLookups,
                workspaceModelDataByWorkspaceId,
                fieldsState,
                subQuestionIsReferenceQuestion: isReferenceQuestion(q),
              })
            : undefined;

        return {
          questionValidations: await questionToErrors(
            q,
            survey,
            tags,
            permissionsContext
          ),
          subQuestionValidations,
        };
      })
    );
  } catch (error) {
    logError(error as Error);
    return [];
  }
};

const maybeSurveyQuestionValidation = (
  message?: SurveyQuestionError | SurveyQuestionWarning
): SurveyQuestionValidation | undefined =>
  message
    ? {
        message,
        severity: message in SurveyQuestionWarning ? 'warning' : 'error',
      }
    : undefined;

export const isRangeValidator = (
  validator: SurveyQuestionValidator
): validator is SurveyQuestionValidator => validator.type === 'range';

export const isRequiredValidator = (
  validator: SurveyQuestionValidator
): validator is SurveyQuestionValidator => validator.type === 'required';

export const isDuplicateFieldQuestion = (
  type: SurveyQuestionType,
  name: string,
  questions?: AllPossibleQuestions[]
): boolean => {
  return Boolean(
    questions?.find(existingQuestion => {
      if (
        existingQuestion.type === type &&
        isAttributeQuestion(existingQuestion)
      ) {
        return name === existingQuestion.properties.attributeName;
      }
      if (
        (isFieldQuestion(existingQuestion) ||
          isHiddenFieldQuestion(existingQuestion)) &&
        type === SurveyQuestionType.FIELD
      ) {
        return name === existingQuestion.properties.fieldName;
      }
      if (isConditionQuestion(existingQuestion)) {
        return isDuplicateFieldQuestion(
          type,
          name,
          existingQuestion.properties.questions
        );
      }
    })
  );
};

export const isFieldSelectedOrNotDuplicated = (
  { type, value }: FieldQuestionData,
  questions?: AllPossibleQuestions[],
  selectedField?: string
) =>
  value === selectedField || !isDuplicateFieldQuestion(type, value, questions);

export const toSelectOption = (
  { value, label, description, isConfidential }: FieldQuestionData,
  i18nLocale: I18nLocale
): SelectOption<string> => {
  if (!isConfidential) {
    return {
      label,
      value,
      description,
    };
  }
  return {
    label,
    value,
    description: t(
      i18nLocale,
      'surveyAdmin:questionAttributes.confidentialFieldDescription',
      'This confidential field will be visible to everyone who can access this survey.'
    ),
    rightIcon: {
      name: IconName.CONTROL,
      color: colors.iconWarning,
    },
  };
};

export const toFieldQuestionData = (
  field: APIFieldAttributes,
  {
    workspaceId,
    parentQuestionIsReferenceQuestion,
    parentQuestionIsReadonly,
    isConfidential,
    readonlyFieldData,
  }: {
    workspaceId: string | null;
    parentQuestionIsReferenceQuestion: boolean;
    parentQuestionIsReadonly?: boolean;
    isConfidential: boolean;
    readonlyFieldData: ReadonlyFieldData;
  }
): FieldQuestionData => {
  const readonly =
    parentQuestionIsReadonly ||
    isReadonlyField({
      workspaceId,
      parentQuestionIsReferenceQuestion,
      fieldName: field.name,
      readonlyFieldData,
    });
  return {
    label: field.label,
    value: field.name,
    type: SurveyQuestionType.FIELD as const,
    fieldType: field.type,
    description: markdownToTextPreview(field.description ?? '') ?? undefined,
    readonly,
    isConfidential,
  };
};

export const attributeToFieldQuestionData = (
  attribute: { label: string; attributeName: string },
  {
    workspaceId,
    parentQuestionIsReferenceQuestion,
    parentQuestionIsReadonly,
    readonlyFieldData,
  }: {
    workspaceId: string | null;
    parentQuestionIsReferenceQuestion: boolean;
    parentQuestionIsReadonly?: boolean;
    readonlyFieldData: ReadonlyFieldData;
  }
): FieldQuestionData => {
  const readonly =
    parentQuestionIsReadonly ||
    isReadonlyField({
      workspaceId,
      parentQuestionIsReferenceQuestion,
      fieldName: attribute.attributeName,
      readonlyFieldData,
    });
  return {
    label: attribute.label,
    value: attribute.attributeName,
    type: SurveyQuestionType.ATTRIBUTE as const,
    readonly,
    isConfidential: false,
  };
};

export const questionValidationsHasMessage = (
  validations: SurveyQuestionValidations | undefined,
  message: SurveyQuestionError | SurveyQuestionWarning
): boolean => {
  return (
    validations?.questionValidations.some(
      validation => validation.message === message
    ) || false
  );
};

export const getQuestionValidationMessageIfExists = (
  validations: SurveyQuestionValidations | undefined,
  message: SurveyQuestionError | SurveyQuestionWarning
): SurveyQuestionError | SurveyQuestionWarning | undefined => {
  return validations?.questionValidations.find(
    validation => validation.message === message
  )?.message;
};

export const getFieldQuestionErrors = (
  question: PersistedSurveyQuestion | UnpersistedSurveyQuestion,
  questionValidations: SurveyQuestionValidations,
  selectOptionsCount: number,
  selectedField: string | undefined,
  i18nLocale: I18nLocale
) => {
  const errors = [];

  if (selectOptionsCount === 0 && !selectedField) {
    errors.push(
      t(
        i18nLocale,
        'surveyAdmin:fieldQuestionErrors.allFieldsAdded',
        'All of the available fields are already added to this survey'
      )
    );
  }

  if (
    questionValidationsHasMessage(
      questionValidations,
      SurveyQuestionError.FIELD_NOT_AVAILABLE
    )
  ) {
    if (isFieldQuestion(question) || isHiddenFieldQuestion(question)) {
      errors.push(
        t(
          i18nLocale,
          'surveyAdmin:fieldQuestionErrors.fieldNotAvailable',
          { fieldName: question.properties.fieldName },
          'The selected field ({{fieldName}}) is not available'
        )
      );
    }
    if (isAttributeQuestion(question)) {
      errors.push(
        t(
          i18nLocale,
          'surveyAdmin:fieldQuestionErrors.attributeNotAvailable',
          { attributeName: question.properties.attributeName },
          'The selected attribute ({{attributeName}}) is not available'
        )
      );
    }
  }

  if (
    questionValidationsHasMessage(
      questionValidations,
      SurveyQuestionError.PARENT_NOT_ALLOWED
    )
  ) {
    errors.push(
      t(
        i18nLocale,
        'surveyAdmin:fieldQuestionErrors.parentNotAllowed',
        'The selected component type cannot have a parent'
      )
    );
  }

  if (
    questionValidationsHasMessage(
      questionValidations,
      SurveyQuestionError.FIELD_NOT_SELECTED
    )
  ) {
    errors.push(
      t(
        i18nLocale,
        'surveyAdmin:fieldQuestionErrors.requiredField',
        REQUIRED_FIELD_MESSAGE
      )
    );
  }

  return errors.length ? errors.join(', ') : undefined;
};

export const getHiddenFieldAnswerErrors = (
  questionValidations: SurveyQuestionValidations,
  fieldLabel?: string
) => {
  return questionValidationsHasMessage(
    questionValidations,
    SurveyQuestionError.INVALID_HIDDEN_ANSWER_VALUE
  )
    ? `Select or enter a value for ${fieldLabel ?? 'hidden field'}.`
    : undefined;
};

export const getQuestionId = (question: AllPossibleQuestions) => {
  return '_id' in question ? question._id : question.key;
};

export const getBarColor = (questionType: SurveyQuestionType) => {
  switch (questionType) {
    case SurveyQuestionType.ATTRIBUTE:
    case SurveyQuestionType.FIELD:
      return colors.blue600;
    case SurveyQuestionType.REFERENCE:
      return colors.brand600;
    case SurveyQuestionType.CHILD:
      return colors.green600;
    case SurveyQuestionType.HIDDEN_FIELD:
      return colors.grey300;
    case SurveyQuestionType.TEXT:
      return colors.grey500;
    case SurveyQuestionType.TAG:
      return colors.purple50;
    default:
      return colors.blue600;
  }
};
export const getSubtitle = (
  question: AllPossibleQuestions,
  i18nLocale: I18nLocale,
  permissionsContext: PermissionContext
) => {
  let subtitle;
  if (
    question.type === SurveyQuestionType.FIELD ||
    question.type === SurveyQuestionType.ATTRIBUTE
  ) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.fieldQuestion',
      'Field question'
    );
  } else if (question.type === SurveyQuestionType.REFERENCE) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.referenceQuestion',
      'Reference question'
    );
  } else if (question.type === SurveyQuestionType.TEXT) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.textSection',
      'Text section'
    );
  } else if (question.type === SurveyQuestionType.HIDDEN_FIELD) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.hiddenField',
      'Hidden field'
    );
  } else if (question.type === SurveyQuestionType.TAG) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.tagQuestion',
      'Tag question'
    );
  } else if (question.type === SurveyQuestionType.CHILD) {
    subtitle = t(
      i18nLocale,
      'surveyAdmin:questions.childQuestion',
      'Child question'
    );
  }
  if (
    isFieldQuestion(question) ||
    isAttributeQuestion(question) ||
    isHiddenFieldQuestion(question)
  ) {
    const name = isAttributeQuestion(question)
      ? question.properties.attributeName
      : question.properties.fieldName;
    if (name) {
      if (dateRangeOperations.fieldNameIsPartOfDateRangeField(name)) {
        subtitle += ` (${dateRangeOperations.extractDateRangeFieldName(name)})`;
      } else {
        subtitle += ` (${name})`;
      }
    }
  } else if (
    isReferenceQuestion(question) &&
    question.properties.componentTypeName &&
    !!question.properties.workspaceId &&
    workspaceAccessControlInterface.canAccessWorkspace(
      permissionsContext,
      question.properties.workspaceId
    )
  ) {
    const direction = question.properties.outgoing
      ? t(
          i18nLocale,
          'surveyAdmin:questions.referenceDirectionTo',
          { componentType: question.properties.componentTypeName },
          "to '{{componentType}}'"
        )
      : t(
          i18nLocale,
          'surveyAdmin:questions.referenceDirectionFrom',
          { componentType: question.properties.componentTypeName },
          "from '{{componentType}}'"
        );
    subtitle += ` (${direction})`;
  } else if (
    isChildQuestion(question) &&
    question.properties.componentTypeName
  ) {
    subtitle += ` (${question.properties.componentTypeName})`;
  }
  return subtitle;
};

export const hasAISuggestions = (
  hasReferenceSuggestionsFeature: boolean,
  aiSuggestionsEnabled: boolean | undefined
) => hasReferenceSuggestionsFeature && aiSuggestionsEnabled === true;

export const questionHasBeenInteractedWith = (
  fieldHasBeenInteractedWith: SurveyEditorState['fieldHasBeenInteractedWith'],
  question: AllPossibleQuestions
): boolean => {
  return Object.values(
    fieldHasBeenInteractedWith.questions[getQuestionId(question)] ?? {}
  ).some(val => val);
};

export const questionHasBeenClosed = (
  fieldHasBeenInteractedWith: SurveyEditorState['fieldHasBeenInteractedWith'],
  question: AllPossibleQuestions
): boolean => {
  return (
    fieldHasBeenInteractedWith.questions[getQuestionId(question)]
      ?.questionViewHasBeenClosed ?? false
  );
};

export const getQuestionQuestionFieldInteractionStatesAllInteracted = (
  questions: AllPossibleQuestions[]
): Record<string, QuestionInteractionState> => {
  const allQuestionIds = questions.flatMap(q => {
    const questionId = getQuestionId(q);
    return isConditionQuestion(q)
      ? [questionId, ...getSubQuestions(q).map(getQuestionId)]
      : [questionId];
  });

  return allQuestionIds.reduce<Record<string, QuestionInteractionState>>(
    (acc, id) => {
      acc[id] = { questionViewHasBeenClosed: true };
      return acc;
    },
    {}
  );
};
